
import React, { useState } from "react";
import {
  Search,
  User,
  Crown,
  Edit,
  Trash2,
  Filter,
  SortAsc,
  SortDesc,
  UserPlus,
  Award,
} from "lucide-react";
import EditPersonModal from "../EditPersonModal";
import { getRankStyle } from "../../utils/rankStyles";
import "./PersonTable.css";

const PersonTable = ({ persons, updatePerson, deletePerson }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterType, setFilterType] = useState("all");
  const [editingPerson, setEditingPerson] = useState(null);
  const [selectedPersons, setSelectedPersons] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  const ranks = ["Member", "Consultant", "Senior", "Lead", "Manager", "Director"];

  const filteredPersons = persons.filter((person) => {
    const matchesSearch = person.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === "all" || person.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const sortedPersons = [...filteredPersons].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    if (typeof aValue === "string") {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortOrder === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleSelectPerson = (personId) => {
    const newSelected = new Set(selectedPersons);
    if (newSelected.has(personId)) {
      newSelected.delete(personId);
    } else {
      newSelected.add(personId);
    }
    setSelectedPersons(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    if (selectedPersons.size === sortedPersons.length) {
      setSelectedPersons(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedPersons(new Set(sortedPersons.map(p => p.id)));
      setShowBulkActions(true);
    }
  };

  const handleBulkRankChange = (newRank) => {
    selectedPersons.forEach(personId => {
      const person = persons.find(p => p.id === personId);
      if (person) {
        updatePerson({ ...person, type: newRank });
      }
    });
    setSelectedPersons(new Set());
    setShowBulkActions(false);
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedPersons.size} selected persons?`)) {
      selectedPersons.forEach(personId => {
        deletePerson(personId);
      });
      setSelectedPersons(new Set());
      setShowBulkActions(false);
    }
  };

  const getRankIcon = (type) => {
    switch (type) {
      case "Consultant":
      case "Senior":
      case "Lead":
        return <Crown size={16} />;
      case "Manager":
      case "Director":
        return <Award size={16} />;
      default:
        return <User size={16} />;
    }
  };

  return (
    <div className="person-table-container">
      <div className="table-header">
        <div className="header-left">
          <h1>Team Directory</h1>
          <div className="table-stats">
            <span className="stat-badge">
              <UserPlus size={16} />
              {persons.length} Total
            </span>
            <span className="stat-badge member">
              <User size={16} />
              {persons.filter(p => p.type === "Member").length} Members
            </span>
            <span className="stat-badge consultant">
              <Crown size={16} />
              {persons.filter(p => p.type !== "Member").length} Leadership
            </span>
          </div>
        </div>

        <div className="header-controls">
          <div className="search-container">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-container">
            <Filter size={20} />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Ranks</option>
              {ranks.map(rank => (
                <option key={rank} value={rank}>{rank}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {showBulkActions && (
        <div className="bulk-actions">
          <div className="bulk-info">
            <span>{selectedPersons.size} selected</span>
          </div>
          <div className="bulk-controls">
            <select
              onChange={(e) => e.target.value && handleBulkRankChange(e.target.value)}
              className="bulk-rank-select"
              defaultValue=""
            >
              <option value="" disabled>Change Rank</option>
              {ranks.map(rank => (
                <option key={rank} value={rank}>{rank}</option>
              ))}
            </select>
            <button
              onClick={handleBulkDelete}
              className="bulk-delete-btn"
            >
              <Trash2 size={16} />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <table className="people-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedPersons.size === sortedPersons.length && sortedPersons.length > 0}
                  onChange={handleSelectAll}
                  className="checkbox"
                />
              </th>
              <th
                className="sortable"
                onClick={() => handleSort("name")}
              >
                Name
                {sortBy === "name" && (
                  sortOrder === "asc" ? <SortAsc size={16} /> : <SortDesc size={16} />
                )}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort("type")}
              >
                Rank
                {sortBy === "type" && (
                  sortOrder === "asc" ? <SortAsc size={16} /> : <SortDesc size={16} />
                )}
              </th>
              <th>ID</th>
              <th>Assigned To</th>
              <th
                className="sortable"
                onClick={() => handleSort("createdAt")}
              >
                Joined
                {sortBy === "createdAt" && (
                  sortOrder === "asc" ? <SortAsc size={16} /> : <SortDesc size={16} />
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedPersons.map((person) => (
              <tr
                key={person.id}
                className={`table-row ${selectedPersons.has(person.id) ? 'selected' : ''}`}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedPersons.has(person.id)}
                    onChange={() => handleSelectPerson(person.id)}
                    className="checkbox"
                  />
                </td>
                <td className="name-cell">
                  <div className="name-container">
                    <div className="avatar" style={getRankStyle(person.type)}>
                      {getRankIcon(person.type)}
                    </div>
                    <span className="name">{person.name}</span>
                  </div>
                </td>
                <td>
                  <div 
                    className="rank-badge"
                    style={getRankStyle(person.type)}
                  >
                    {getRankIcon(person.type)}
                    {person.type}
                  </div>
                </td>
                <td className="id-cell">#{person.id}</td>
                <td className="assignment-cell">
                  {person.assignedTo && person.assignedTo !== 'None' ? (
                    <span className="assignment">{person.assignedTo}</span>
                  ) : (
                    <span className="no-assignment">Independent</span>
                  )}
                </td>
                <td className="date-cell">
                  {person.createdAt ? new Date(person.createdAt).toLocaleDateString() : 'N/A'}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn edit"
                      onClick={() => setEditingPerson(person)}
                      title="Edit Person"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => {
                        if (window.confirm(`Delete ${person.name}?`)) {
                          deletePerson(person.id);
                        }
                      }}
                      title="Delete Person"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sortedPersons.length === 0 && (
          <div className="empty-state">
            <User size={64} className="empty-icon" />
            <h3>No team members found</h3>
            <p>
              {searchTerm
                ? `No results for "${searchTerm}"`
                : "No team members have been added yet."}
            </p>
          </div>
        )}
      </div>

      {editingPerson && (
        <EditPersonModal
          person={editingPerson}
          persons={persons}
          onSave={updatePerson}
          onClose={() => setEditingPerson(null)}
        />
      )}
    </div>
  );
};

export default PersonTable;
